import React, { useState, useEffect } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { CatalogService } from '../../services/catalogService';
import { type Duenio } from '../../types/catalog';
import { EditDuenio } from '../../features/catalogo/duenios/components';
import { type DuenioRequest } from '../../features/catalogo/duenios/types/duenio';
import { DuenioService } from '../../features/catalogo/duenios/services/duenioService';

interface DuenioSelectProps {
  value: number | string;
  onChange: (value: number) => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  allowCreate?: boolean;
}

export const DuenioSelect: React.FC<DuenioSelectProps> = ({
  value,
  onChange,
  label = 'Dueño',
  error,
  required = false,
  disabled = false,
  fullWidth = true,
  allowCreate = true,
}) => {
  const [duenios, setDuenios] = useState<Duenio[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    loadDuenios();
  }, []);

  const loadDuenios = async () => {
    try {
      setLoading(true);
      const data = await CatalogService.getDuenios();
      setDuenios(data);
    } catch (error) {
      console.error('Error loading duenios:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNewDuenio = async (request: DuenioRequest) => {
    const saved = await DuenioService.saveDuenio(request);
    await loadDuenios();
    onChange(saved.id);
    setCreateDialogOpen(false);
  };

  const options = duenios.map(duenio => ({
    value: duenio.id,
    label: `${duenio.nombres} ${duenio.apellidos}`,
  }));

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flex: 1 }}>
          <SelectFieldComponent
            label={label}
            value={value}
            onChange={(val) => onChange(Number(val))}
            options={options}
            error={error}
            required={required}
            disabled={disabled || loading}
            fullWidth={fullWidth}
            placeholder={loading ? 'Cargando...' : 'Seleccione un dueño'}
          />
        </Box>
        {allowCreate && !disabled && (
          <Tooltip title="Nuevo Dueño">
            <IconButton
              size="small"
              color="primary"
              onClick={() => setCreateDialogOpen(true)}
              sx={{ mt: 0.5 }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <EditDuenio
        open={createDialogOpen}
        duenio={null}
        onClose={() => setCreateDialogOpen(false)}
        onSave={handleSaveNewDuenio}
        title="Nuevo Dueño"
      />
    </>
  );
};
